import { Component, OnDestroy, OnInit } from '@angular/core';
import { formatNumber } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { UtilityService } from '@services/utility.service';
import { PermissionsService } from '@services/permissions.service';
import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { PlantDetail } from '@/Interface/plantDetail';
import { InverterDetail } from '@/Interface/inverterDetail';
import { HeatMapIntterface } from '@/Interface/power';
import { rotateAnimation } from '@pages/plant-detail/plant-detail.component';

type ProfileTab = 'devices' | 'roi' | 'panels';

// Trimmed copy of the plant detail page: the header and plant summary, plus
// only the Devices, ROI and Panels tabs. ROI logic (graphs, cost and
// prediction parameter modals) is carried over from plant-detail as-is.
@Component({
    selector: 'app-plant-profile',
    templateUrl: './plant-profile.component.html',
    styleUrls: ['./plant-profile.component.scss'],
    animations: [rotateAnimation('0.5s')]
})
export class PlantProfileComponent implements OnInit, OnDestroy {
    readonly tabs: { key: ProfileTab; label: string }[] = [
        { key: 'devices', label: 'Devices' },
        { key: 'roi', label: 'ROI' },
        { key: 'panels', label: 'Panels' }
    ];
    activeTab: ProfileTab = 'devices';
    private tabLoaded = false;

    getStationCode: string;
    selectedPlantName: string;
    plantDetail: PlantDetail;
    loader = false;
    isReloadingBtn = false;
    permissions = { ...DEFAULT_PERMISSIONS };
    permissionsReady = false;
    private destroy$ = new Subject<void>();

    // ROI
    roiChart: any;
    monthlyRoi = false;
    yearlyRoi = true;
    installationCost: string;
    categories = [];
    seriesData = [];
    predictedData1 = [];
    actualCategories = [];
    allCategories = [];
    yearRangeTitle = '';
    breakEvenYear = '';
    responseMessage = '';
    roiParameters: any = {};
    originalCost: number;
    predictionParameterForm: FormGroup;

    // Panels
    inverters: { devId: number; name: string }[] = [];
    selectedInverterId: number = null;
    invertersLoading = false;

    pvStrings: any[] = [];
    selectedPvId: number = null;
    pvStringsLoading = false;

    panels: any[] = [];
    panelsLoading = false;
    readonly skeletonRows = Array.from({ length: 6 });
    private panelsRequestId = 0;
    selectedPanelIds: number[] = [];

    // Fetched from the backend (GET /panel-statuses/dropdown/) — "All
    // Statuses" is prepended client-side since it isn't a real PanelStatus.
    statusOptions: { value: string; label: string }[] = [{ value: null, label: 'All Statuses' }];
    statusFilter: string = null;
    panelSearchTerm = '';
    currentPage = 1;
    totalPages = 1;
    totalRecords = 0;
    nextPage: number = null;
    previousPage: number = null;
    private readonly panelsPageSize = 10;
    private panelSearchSubject = new Subject<string>();

    brands: { id: number; name: string }[] = [];
    brandsLoading = false;
    addPanelRows: { sr_no: string; brand: number; series_name: string; capacity_kw: number }[] = [];
    addPanelSubmitting = false;

    bulkActionType: 'transfer' | 'dispose' | 'sale' | null = null;
    bulkActionReason = '';
    bulkActionSubmitting = false;
    transferPlants: { id: number; stationCode: string; stationName: string }[] = [];
    transferInverters: any[] = [];
    transferPvStrings: any[] = [];
    selectedTransferPlantCode: string = null;
    selectedTransferInverterId: number = null;
    selectedTransferPvId: number = null;

    historyPanel: any = null;

    heatMapList: HeatMapIntterface;

    get selectedInverterName(): string {
        return this.inverters.find((inv) => inv.devId === this.selectedInverterId)?.name;
    }

    get totalPanels(): number {
        return this.totalRecords;
    }

    get allPanelsSelected(): boolean {
        return this.panels.length > 0 && this.selectedPanelIds.length === this.panels.length;
    }

    get selectedPvLabel(): string {
        return this.pvStrings.find((pv) => pv.id === this.selectedPvId)?.label ?? '';
    }

    constructor(
        private _utilityService: UtilityService,
        private permService: PermissionsService,
        private formBuilder: FormBuilder,
        private modalService: NgbModal,
        private toaster: ToastrService,
        private _route: ActivatedRoute,
        private _router: Router
    ) { }

    ngOnInit(): void {
        this.getComponentPermissions();

        this._utilityService.selectedProduct$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.getStationCode = value;
        });
        this._utilityService.selectedPlantName$.pipe(takeUntil(this.destroy$)).subscribe((plantName: string) => {
            this.selectedPlantName = plantName;
        });
        // The URL is the source of truth: on a refresh or a deep link the
        // service subjects are empty, so seed them from the :id param.
        const routeStationCode = this._route.snapshot.paramMap.get('id');
        if (routeStationCode && String(this.getStationCode) !== routeStationCode) {
            this.getStationCode = routeStationCode;
            this._utilityService.setProduct(routeStationCode);
            this._utilityService.updateSelectedPlantName('');
        }

        this.predictionParameterForm = this.formBuilder.group({
            cost: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(11)]],
            baseProduction: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(11)]],
            nextYearProduction: [null, [Validators.required, Validators.minLength(3), Validators.maxLength(11)]],
            tariffRate: [null, [Validators.required, Validators.pattern(/^100$|^\d{1,2}(\.\d{1,2})?$/)]],
            dipPercentage: [null, [Validators.required, Validators.pattern(/^100$|^\d{1,2}(\.\d{1,2})?$/)]]
        });

        this.getPlantDetailList();
        this.getHeatMap();

        // The open tab lives in the URL (/plant-profile/:id/:tab), so a
        // refresh, a shared link or Back/Forward lands on the same tab.
        this._route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            const tab = params.get('tab') as ProfileTab;
            if (this.tabs.some((t) => t.key === tab)) {
                this.showTab(tab);
            } else {
                this._router.navigate(this.tabLink('devices'), { replaceUrl: true });
            }
        });

        this.panelSearchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((term: string) => {
            this.panelSearchTerm = term;
            this.currentPage = 1;
            this.getPanels();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async getComponentPermissions() {
        const url = this.permService.getCurrentUrl();
        const menuFromLocal = await this.permService.waitForMenuInLocalStorage();
        // Until Plant Profile has its own menu entry, inherit the Plants
        // entry's permissions — the same ones plant detail uses for these actions.
        const matchedMenu =
            this.permService.findMenuPermissions(menuFromLocal, url) ||
            this.permService.findMenuPermissions(menuFromLocal, '/plant-management/plants');
        this.permissions = matchedMenu
            ? {
                canAdd: !!matchedMenu.canAdd,
                canEdit: !!matchedMenu.canEdit,
                canView: !!matchedMenu.canView,
                canDelete: !!matchedMenu.canDelete
            }
            : { ...DEFAULT_PERMISSIONS };
        this.permissionsReady = true;
    }

    tabLink(tab: ProfileTab): any[] {
        return ['/plant-management/plant-profile', this._route.snapshot.paramMap.get('id'), tab];
    }

    private showTab(tab: ProfileTab): void {
        if (tab === this.activeTab && this.tabLoaded) {
            return;
        }
        this.tabLoaded = true;
        this.activeTab = tab;
        // ROI and Panels show "Coming soon" for now; re-enable with their templates.
        // if (tab === 'roi') {
        //     this.getYearlyRoiGraph();
        // } else if (tab === 'panels' && !this.inverters.length) {
        //     this.getInverters();
        // }
    }

    reloadPlantDetails(): void {
        this.isReloadingBtn = true;
        this.getPlantDetailList();
        this.getHeatMap();
        // if (this.activeTab === 'roi') {
        //     this.yearlyRoi ? this.getYearlyRoiGraph() : this.getMonthlyRoiGraph();
        // } else if (this.activeTab === 'panels') {
        //     this.getInverters();
        // }
        // Devices reloads itself off the [reloadDevices] input.
        setTimeout(() => (this.isReloadingBtn = false), 500);
    }

    showPlantList(): void {
        this._router.navigate(['/plant-management/plant-profile']);
    }

    getPlantDetailList(): void {
        this._utilityService.setPlantDetailList(this.getStationCode).subscribe((res) => {
            if (res && res.data?.length) {
                this.plantDetail = res.data[0];
                // On a cold open the header has no name yet — take it from the response.
                if (!this.selectedPlantName && this.plantDetail?.plantName) {
                    this.selectedPlantName = this.plantDetail.plantName;
                    this._utilityService.updateSelectedPlantName(this.plantDetail.plantName);
                }
                this.plantDetail.costFormatted = this.convertToThousandsMillions(this.plantDetail.cost || 0);
            }
        });
    }

    // Device connection counts and per-inverter colours, shared by the
    // Devices chips and the Panels heat map.
    getHeatMap(): void {
        this._utilityService.getHeatMapList(this.getStationCode).subscribe((res) => {
            if (res) {
                this.heatMapList = res;
            }
        });
    }

    getHeatColor(colorValue: string): string {
        if (colorValue === 'Green') {
            return '#6FCF97';
        }
        if (colorValue === 'Red' || colorValue === 'Faulty') {
            return '#E8837A';
        }
        return '#D5DCE5';
    }

    isSelectedInverter(devName: string): boolean {
        return !!devName && devName === this.selectedInverterName;
    }

    selectInverterByName(devName: string): void {
        const inverter = this.inverters.find((inv) => inv.name === devName);
        if (inverter) {
            this.onInverterChange(inverter.devId);
        }
    }

    convertToThousandsMillions(valueInPKR, limitThousands = 10000, limitMillions = 1000000) {
        if (valueInPKR >= limitMillions) {
            return `${(valueInPKR / 1000000).toFixed(2)} million PKR`;
        } else if (valueInPKR >= limitThousands) {
            return `${(valueInPKR / 1000).toFixed(2)}k PKR`;
        }
        return `${valueInPKR.toFixed(2)} PKR`;
    }

    // ==================== ROI ====================

    formatTimestamp = (timestamp: number | string): string => {
        let date = typeof timestamp === 'string' ? new Date(timestamp) : new Date(Number(timestamp) * 1000);
        if (this.yearlyRoi === true) {
            return date.toLocaleString('default', { year: 'numeric' });
        }
        if (this.monthlyRoi === true) {
            date = new Date(timestamp);
            return date.toLocaleString('default', { month: 'short', year: 'numeric' });
        }
    };

    getYearRange = (timestamps: number[] | string[]): string => {
        const years = timestamps.map((timestamp) => new Date(timestamp).getFullYear());
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);
        return minYear === maxYear ? `(${maxYear})` : `(${minYear} - ${maxYear})`;
    };

    private setMissingRoiMessage(): void {
        if (!this.seriesData || this.seriesData.length === 0) {
            this.responseMessage = 'Please add Installation Cost and Ensure Plant Association with Tariff Type and Tariff Company.';
        }
    }

    getYearlyRoiGraph(): void {
        this.loader = true;
        this._utilityService.getYearlyRoiGraph(this.getStationCode).subscribe({
            next: (res) => {
                this.loader = false;
                this.monthlyRoi = false;
                this.yearlyRoi = true;
                if (res && res.status === 200) {
                    const actualDataMap = new Map(res.data['yearly_roi'].map((item) => [this.formatTimestamp(item['collectTime']), item['payback']]));
                    const predictedDataMap = new Map(res.data['predicted_roi'].map((item) => [this.formatTimestamp(item['collectTime']), item['predicted_payback']]));
                    this.installationCost = this.convertToThousandsMillions(res.installation_cost);
                    this.roiParameters = res.parameters;
                    this.actualCategories = [...actualDataMap.keys()];
                    this.allCategories = [...new Set([...this.actualCategories, ...predictedDataMap.keys()])].sort();
                    this.categories = this.allCategories;
                    this.seriesData = this.categories.map((category) => actualDataMap.get(category) || null);
                    this.predictedData1 = this.categories.map((category) => predictedDataMap.get(category) || null);
                    this.yearRangeTitle = this.getYearRange(this.allCategories);
                    this.breakEvenYear = res.break_even_year;
                    this.setMissingRoiMessage();
                    this.RoiChartinit();
                } else {
                    this.seriesData = [];
                    this.categories = [];
                    this.yearRangeTitle = '';
                    this.setMissingRoiMessage();
                }
            },
            error: (error) => {
                this.loader = false;
                this.seriesData = [];
                this.responseMessage = error?.error?.message || 'Unable to load ROI.';
            }
        });
    }

    getMonthlyRoiGraph(): void {
        this.loader = true;
        this._utilityService.monthlyRoiGraph(this.getStationCode).subscribe({
            next: (res) => {
                this.loader = false;
                this.monthlyRoi = true;
                this.yearlyRoi = false;
                if (res && res.status === 200) {
                    this.installationCost = this.convertToThousandsMillions(res.installation_cost);
                    this.seriesData = res.data.map((item) => item['payback']);
                    this.categories = res.data.map((item) => this.formatTimestamp(item['collectTime']));
                    this.yearRangeTitle = this.getYearRange(res.data.map((item) => item['collectTime']));
                    this.setMissingRoiMessage();
                    this.RoiChartinit();
                } else {
                    this.seriesData = [];
                    this.categories = [];
                    this.yearRangeTitle = '';
                    this.setMissingRoiMessage();
                }
            },
            error: (error) => {
                this.loader = false;
                this.seriesData = [];
                this.responseMessage = error?.error?.message || 'Unable to load ROI.';
            }
        });
    }

    RoiChartinit(): void {
        const actualColor = '#F2A93B';
        const predictedColor = '#3FAE6A';
        const series = [{ name: 'Payback', data: this.seriesData }];
        if (this.yearlyRoi && this.predictedData1.some((v) => v !== null)) {
            series.push({ name: 'Predicted Payback', data: this.predictedData1 });
        }
        const hasBreakEven = this.yearlyRoi && this.breakEvenYear && this.categories.includes(String(this.breakEvenYear));

        this.roiChart = {
            series,
            chart: {
                type: 'area',
                height: 420,
                fontFamily: 'inherit',
                toolbar: { show: false },
                zoom: { enabled: false }
            },
            colors: [actualColor, predictedColor],
            stroke: { curve: 'smooth', width: [3, 2], dashArray: [0, 6] },
            fill: {
                type: 'gradient',
                gradient: { shadeIntensity: 1, opacityFrom: [0.35, 0.08], opacityTo: [0.02, 0], stops: [0, 100] }
            },
            // One size for every series: per-series sizes (e.g. [4, 0]) make
            // ApexCharts pin the hover crosshair and tooltip to the left edge.
            markers: { size: 0, strokeWidth: 2, strokeColors: '#fff', hover: { size: 5 } },
            dataLabels: { enabled: false },
            legend: {
                position: 'top',
                horizontalAlign: 'left',
                fontSize: '13px',
                markers: { radius: 12 },
                itemMargin: { horizontal: 12 }
            },
            grid: {
                borderColor: '#EEF1F4',
                strokeDashArray: 4,
                padding: { left: 8, right: 16 }
            },
            xaxis: {
                categories: this.categories,
                axisBorder: { show: false },
                axisTicks: { show: false },
                tooltip: { enabled: false },
                labels: { rotate: 0, hideOverlappingLabels: true, style: { colors: '#8A94A6', fontSize: '12px' } }
            },
            yaxis: {
                labels: {
                    style: { colors: '#8A94A6', fontSize: '12px' },
                    formatter: (val: number) => this.compactAmount(val)
                }
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: { formatter: (val: number) => (val === null || val === undefined ? '—' : `${this.compactAmount(val)} PKR`) }
            },
            annotations: {
                yaxis: [{
                    y: 0,
                    borderColor: '#B42318',
                    strokeDashArray: 0,
                    label: {
                        text: 'Break-even',
                        position: 'left',
                        textAnchor: 'start',
                        borderColor: 'transparent',
                        style: { color: '#B42318', background: 'transparent', fontSize: '11px' }
                    }
                }],
                xaxis: hasBreakEven
                    ? [{
                        x: String(this.breakEvenYear),
                        borderColor: '#2B2B2B',
                        strokeDashArray: 4,
                        label: {
                            text: `Breakeven ${this.breakEvenYear}`,
                            orientation: 'horizontal',
                            borderColor: '#2B2B2B',
                            style: { color: '#fff', background: '#2B2B2B', fontSize: '11px' }
                        }
                    }]
                    : []
            }
        };
    }

    // 1234567 -> "1.23M", -250000000 -> "-250M"
    compactAmount(value: number): string {
        if (value === null || value === undefined || isNaN(value)) {
            return '';
        }
        const abs = Math.abs(value);
        const sign = value < 0 ? '-' : '';
        if (abs >= 1e9) {
            return `${sign}${+(abs / 1e9).toFixed(2)}B`;
        }
        if (abs >= 1e6) {
            return `${sign}${+(abs / 1e6).toFixed(2)}M`;
        }
        if (abs >= 1e3) {
            return `${sign}${+(abs / 1e3).toFixed(2)}K`;
        }
        return `${sign}${+abs.toFixed(2)}`;
    }


    reCalculateRoi(): void {
        this.loader = true;
        this._utilityService.reCalculateRoi(this.plantDetail.stationCode).subscribe((res) => {
            this.loader = false;
            if (res && res.status === 200) {
                setTimeout(() => this.getMonthlyRoiGraph(), 1500);
            } else {
                this.toaster.error(res.message);
            }
        });
    }

    openUpdateCostModal(modal: any): void {
        this.originalCost = this.plantDetail.cost;
        this.modalService.open(modal, { windowClass: 'complainModelClass' });
    }

    openPredictionModal(modal: any): void {
        this.originalCost = this.plantDetail.cost;
        this.modalService.open(modal, { windowClass: 'complainModelClass' });
        const cost = this.originalCost ? formatNumber(this.originalCost, 'en-US', '2.0-0') : this.originalCost;
        setTimeout(() => {
            this.predictionParameterForm.patchValue(
                this.roiParameters
                    ? {
                        cost,
                        baseProduction: this.roiParameters['base'],
                        nextYearProduction: this.roiParameters['next_year_production'],
                        tariffRate: this.roiParameters['tariff_rate'],
                        dipPercentage: this.roiParameters['dip_percentage']
                    }
                    : { cost }
            );
        }, 100);
    }

    handleCancel(dismiss: Function): void {
        this.plantDetail.cost = this.originalCost;
        dismiss('Cancel click');
    }

    updateInstallationCost(): void {
        this.loader = true;
        this._utilityService.updateInstallationCost(this.plantDetail.stationCode, this.plantDetail.cost).subscribe((res) => {
            this.loader = false;
            if (res && res.status === 200) {
                this.plantDetail.costFormatted = this.convertToThousandsMillions(res.data.cost);
                Swal.fire({
                    icon: 'success',
                    title: 'Cost Updated',
                    text: 'ROI Graph needs to be processed again.',
                    showConfirmButton: false,
                    timer: 1600
                });
                setTimeout(() => this.getYearlyRoiGraph(), 2000);
            } else {
                this.toaster.error(res.message);
                this.plantDetail.costFormatted = this.convertToThousandsMillions(this.plantDetail.cost);
            }
        });
    }

    onPredictionParamters(): void {
        if (!this.predictionParameterForm.valid) {
            this.toaster.error('Please add Valid Form Data');
            this.predictionParameterForm.reset();
            return;
        }
        const value = this.predictionParameterForm.value;
        const payload = {
            stationCode: this.plantDetail.stationCode,
            cost: value.cost ? parseFloat(String(value.cost).replace(/,/g, '')) : null,
            baseProduction: parseInt(value.baseProduction),
            nextYearProduction: parseInt(value.nextYearProduction),
            tariffRate: parseFloat(value.tariffRate),
            dipPercentage: parseFloat((value.dipPercentage / 100).toFixed(3))
        };
        this.loader = true;
        this._utilityService.predictedRoiApi(payload).subscribe((res) => {
            this.loader = false;
            if (res && res.status === 201) {
                Swal.fire({ icon: 'success', title: res.message, showConfirmButton: false, timer: 1600 });
                setTimeout(() => this.getYearlyRoiGraph(), 2000);
            } else {
                this.predictionParameterForm.reset();
                this.toaster.error(res.message);
            }
        });
    }

    // ==================== Panels ====================
    // Plant -> Inverter -> String(PV) -> Panel. Panels have no direct edit/
    // delete — only transfer/dispose/sale, each going through the approval
    // workflow (request created pending -> approve/reject).

    getInverters(): void {
        this.invertersLoading = true;
        this._utilityService.inverterPrimeData(this.getStationCode).subscribe({
            next: (res) => {
                this.inverters = (res?.data || []).map((item: InverterDetail) => ({
                    devId: item.basicInfo.devId,
                    name: item.basicInfo.deviceName
                }));
                // No inverter is pre-selected — the listing shows every panel on
                // the plant by default; picking an inverter just narrows it.
                this.invertersLoading = false;
                this.getPanels();
            },
            error: () => {
                this.inverters = [];
                this.invertersLoading = false;
            }
        });
        if (this.statusOptions.length === 1) {
            this.loadPanelStatuses();
        }
    }

    loadPanelStatuses(): void {
        this._utilityService.getPanelStatusesDropdown().subscribe({
            next: (res: { value: string; label: string }[]) => {
                this.statusOptions = [{ value: null, label: 'All Statuses' }, ...(res || [])];
            },
            error: () => {
                // Keep the "All Statuses" fallback — the status filter just
                // won't offer specific statuses until the next successful load.
            }
        });
    }

    onInverterChange(devId: number): void {
        this.selectedInverterId = devId;
        this.selectedPvId = null;
        this.pvStrings = [];
        this.currentPage = 1;
        if (!devId) {
            this.getPanels();
            return;
        }
        this.pvStringsLoading = true;
        this._utilityService.getPvStringsDropdown(devId).subscribe({
            next: (res) => {
                this.pvStrings = res || [];
                this.pvStringsLoading = false;
                this.getPanels();
            },
            error: () => {
                this.pvStrings = [];
                this.pvStringsLoading = false;
            }
        });
    }

    onPvChange(pvId: number): void {
        this.selectedPvId = pvId;
        this.currentPage = 1;
        this.getPanels();
    }

    onStatusFilterChange(status: string): void {
        this.statusFilter = status;
        this.currentPage = 1;
        this.getPanels();
    }

    searchPanels(event: Event): void {
        this.panelSearchSubject.next((event.target as HTMLInputElement)?.value || '');
    }

    goToPage(page: number): void {
        if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
            this.currentPage = page;
            this.getPanels();
        }
    }

    navigatePage(direction: 'prev' | 'next'): void {
        if (direction === 'prev' && this.previousPage !== null) {
            this.goToPage(this.previousPage);
        } else if (direction === 'next' && this.nextPage !== null) {
            this.goToPage(this.nextPage);
        }
    }

    getPanels(): void {
        if (!this.getStationCode) {
            this.panels = [];
            return;
        }
        this.panelsLoading = true;
        this.selectedPanelIds = [];
        // Filters can change faster than the API answers; only the latest
        // request may update the table.
        const requestId = ++this.panelsRequestId;
        const filters: any = {
            plant: this.getStationCode,
            page: this.currentPage,
            size: this.panelsPageSize
        };
        if (this.selectedInverterId) {
            filters.inverter = this.selectedInverterId;
        }
        if (this.selectedPvId) {
            filters.string = this.selectedPvId;
        }
        if (this.statusFilter) {
            filters.status = this.statusFilter;
        }
        if (this.panelSearchTerm) {
            filters.search = this.panelSearchTerm;
        }
        this._utilityService.getPanelsList(filters).subscribe({
            next: (res: any) => {
                if (requestId !== this.panelsRequestId) {
                    return;
                }
                this.panels = res?.results || [];
                const pagination = res?.pagination || {};
                this.totalRecords = pagination.total_records || 0;
                this.totalPages = pagination.total_pages || 1;
                this.currentPage = pagination.current_page || 1;
                this.nextPage = pagination.next_page ?? null;
                this.previousPage = pagination.previous_page ?? null;
                this.panelsLoading = false;
            },
            error: () => {
                if (requestId !== this.panelsRequestId) {
                    return;
                }
                this.panels = [];
                this.panelsLoading = false;
            }
        });
    }

    // Disposed/sold panels have pv=null — last_pv_detail is their last known
    // inverter/string, kept only for this "where did it come from" display.
    panelLocationLabel(panel: any): string {
        const detail = panel?.pv_detail || panel?.last_pv_detail;
        if (!detail) {
            return '—';
        }
        const suffix = panel?.pv_detail ? '' : ' (last known)';
        return `${detail.devName} / ${detail.label}${suffix}`;
    }

    toggleAllPanels(checked: boolean): void {
        this.selectedPanelIds = checked ? this.panels.map((p) => p.id) : [];
    }

    togglePanelSelection(panel: any): void {
        const idx = this.selectedPanelIds.indexOf(panel.id);
        if (idx === -1) {
            this.selectedPanelIds.push(panel.id);
        } else {
            this.selectedPanelIds.splice(idx, 1);
        }
    }

    isPanelActionAvailable(panel: any, action: string): boolean {
        return panel?.user_permissions?.[action] === true && panel?.panel_actions?.includes(action);
    }

    // The backend ties "reject" to the same 'approve' permission flag as
    // approve (there's no separate reject right) — user_permissions has no
    // 'reject' key, so this checks 'approve' against the 'reject' status-action.
    canReject(panel: any): boolean {
        return panel?.user_permissions?.approve === true && panel?.panel_actions?.includes('reject');
    }

    getPanelStatusBadgeClass(status: string): string {
        const map: Record<string, string> = {
            active: 'badge-success',
            pending_transfer: 'badge-warning',
            pending_dispose: 'badge-warning',
            pending_sale: 'badge-warning',
            sold: 'badge-gray',
            disposed: 'badge-gray'
        };
        return map[status] || 'badge-blue';
    }

    // --- Add panel (bulk create on the selected string) ---

    addPanel(modal: any): void {
        if (!this.selectedPvId) {
            this.toaster.error('Select a string first.');
            return;
        }
        this.addPanelRows = [{ sr_no: '', brand: null, series_name: '', capacity_kw: null }];
        if (!this.brands.length) {
            this.loadBrands();
        }
        this.modalService.open(modal, { windowClass: 'complainModelClass' });
    }

    loadBrands(): void {
        this.brandsLoading = true;
        this._utilityService.getBrandsDropdown().subscribe({
            next: (res) => {
                this.brands = res || [];
                this.brandsLoading = false;
            },
            error: () => {
                this.brands = [];
                this.brandsLoading = false;
            }
        });
    }

    addPanelRow(): void {
        this.addPanelRows.push({ sr_no: '', brand: null, series_name: '', capacity_kw: null });
    }

    removePanelRow(index: number): void {
        this.addPanelRows.splice(index, 1);
    }

    submitAddPanels(modalRef: any): void {
        const validRows = this.addPanelRows.filter((r) => r.sr_no && r.brand && r.series_name && r.capacity_kw != null);
        if (!validRows.length) {
            this.toaster.error('Fill in at least one complete panel row.');
            return;
        }
        this.addPanelSubmitting = true;
        this._utilityService.bulkCreatePanels({ pv: this.selectedPvId, panels: validRows }).subscribe({
            next: () => {
                this.addPanelSubmitting = false;
                this.toaster.success('Panel(s) created');
                modalRef.close();
                this.getPanels();
            },
            error: (err) => {
                this.addPanelSubmitting = false;
                this.toaster.error(err?.error?.error || err?.error?.message || 'Failed to create panels.');
            }
        });
    }

    // --- Bulk transfer / dispose / sale ---

    openBulkAction(type: 'transfer' | 'dispose' | 'sale', modal: any): void {
        if (!this.selectedPanelIds.length) {
            this.toaster.error('Select at least one panel first.');
            return;
        }
        this.bulkActionType = type;
        this.bulkActionReason = '';
        this.selectedTransferPlantCode = null;
        this.selectedTransferInverterId = null;
        this.selectedTransferPvId = null;
        this.transferInverters = [];
        this.transferPvStrings = [];
        if (type === 'transfer' && !this.transferPlants.length) {
            this._utilityService.getStationsDropDown().subscribe((res: any) => {
                this.transferPlants = res?.data || [];
            });
        }
        this.modalService.open(modal, { windowClass: 'complainModelClass' });
    }

    onTransferPlantChange(stationCode: string): void {
        this.selectedTransferPlantCode = stationCode;
        this.selectedTransferInverterId = null;
        this.selectedTransferPvId = null;
        this.transferPvStrings = [];
        if (!stationCode) {
            this.transferInverters = [];
            return;
        }
        this._utilityService.getPlantInverters(stationCode).subscribe((res: any) => {
            this.transferInverters = res || [];
        });
    }

    onTransferInverterChange(devId: number): void {
        this.selectedTransferInverterId = devId;
        this.selectedTransferPvId = null;
        if (!devId) {
            this.transferPvStrings = [];
            return;
        }
        this._utilityService.getPvStringsDropdown(devId).subscribe((res: any) => {
            this.transferPvStrings = res || [];
        });
    }

    submitBulkAction(modalRef: any): void {
        this.bulkActionSubmitting = true;
        const body: any = { panel_ids: this.selectedPanelIds, reason: this.bulkActionReason };
        let request$;
        if (this.bulkActionType === 'transfer') {
            if (!this.selectedTransferPvId) {
                this.toaster.error('Select a destination string.');
                this.bulkActionSubmitting = false;
                return;
            }
            body.destination_pv = this.selectedTransferPvId;
            request$ = this._utilityService.bulkTransferPanels(body);
        } else if (this.bulkActionType === 'dispose') {
            request$ = this._utilityService.bulkDisposePanels(body);
        } else {
            request$ = this._utilityService.bulkSellPanels(body);
        }
        request$.subscribe({
            next: () => {
                this.bulkActionSubmitting = false;
                this.toaster.success('Request submitted for approval.');
                modalRef.close();
                this.getPanels();
            },
            error: (err) => {
                this.bulkActionSubmitting = false;
                this.toaster.error(err?.error?.error || err?.error?.message || 'Failed to submit request.');
            }
        });
    }

    approvePanelRequest(panel: any): void {
        if (!panel.pending_request_id) {
            return;
        }
        this._utilityService.approvePanelRequest(panel.pending_request_id).subscribe({
            next: () => {
                this.toaster.success('Request approved');
                this.getPanels();
            },
            error: (err) => this.toaster.error(err?.error?.error || 'Failed to approve.')
        });
    }

    rejectPanelRequest(panel: any): void {
        if (!panel.pending_request_id) {
            return;
        }
        this._utilityService.rejectPanelRequest(panel.pending_request_id).subscribe({
            next: () => {
                this.toaster.success('Request rejected');
                this.getPanels();
            },
            error: (err) => this.toaster.error(err?.error?.error || 'Failed to reject.')
        });
    }

    viewPanelHistory(panel: any, modal: any): void {
        this.historyPanel = panel;
        this.modalService.open(modal, { windowClass: 'complainModelClass' });
    }
}
